import { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { Box, Button, Divider, Paper, Typography } from "@material-ui/core";
import ButtonWithLoading from "components/ButtonWithLoading";
import MxheroSharesSkeleton from "./MxheroSharesSkeleton";
import request, { getErrorCode } from "utils/apiRequestWithErrorCode";
import { getUser } from "utils/authentication";
import useOrganization from "utils/useOrganization";
import { useStyles } from "../styles";
import SubdomainInput from "./SubdomainInput";
import RetentionOptions from "./RetentionOptions";
import AuthenticationOptions from "./AuthenticationOptions";
import { MxheroShareConfig } from "@dashboard-v3/api";
import { isBlank } from "utils/string";
import EndUserPermissions from "./EndUserPermissions";
import SectionHeader from "components/SectionHeader";
import RegionOptions from "./RegionOptions";

export enum Status {
  Loading = "LOADING",
  Disabled = "DISABLED",
  Saving = "SAVING",
  Enabled = "ENABLED",
}

export default function MxheroSharesSettings() {
  const { t } = useTranslation("mxheroShares");
  const { enqueueSnackbar } = useSnackbar();
  const { organization, revalidateOrganization } = useOrganization();
  const [formError, setFormError] = useState<string>();
  const [form, setForm] = useState<MxheroShareConfig>(initForm);
  const [status, setStatus] = useState<Status>(Status.Loading);
  const classes = useStyles();
  const isEdit = !!organization?.mxheroStorage?.hostname;

  useEffect(() => {
    if (organization?.mxheroStorage) {
      setForm(() => {
        const { hostname, retention, region, ownerPermissions, ...auth } =
          organization.mxheroStorage;

        if (!hostname) return initForm();

        return {
          retention,
          region,
          subdomain: hostname,
          ownerPermissions,
          ...auth,
        };
      });

      setStatus(() => {
        const { hostname } = organization.mxheroStorage;
        if (hostname) return Status.Enabled;
        return Status.Disabled;
      });
    }
  }, [organization?.mxheroStorage]);

  function updateForm(update: Partial<MxheroShareConfig>) {
    setForm(prev => ({ ...prev, ...update }));
  }

  async function onSubmit() {
    setStatus(Status.Saving);
    try {
      const { retention, subdomain, ownerPermissions, region, ...authMethods } =
        form;
      const method = isEdit ? "PATCH" : "POST";
      const body = {
        retention,
        ownerPermissions,
        ...authMethods,
        ...(!isEdit && { subdomain, region }),
      };

      await request(method, "/mxhero-shares-config", body);
      await revalidateOrganization();

      enqueueSnackbar(t("settings.success"), { variant: "success" });

      setStatus(Status.Enabled);
    } catch (error) {
      const errorCode = getErrorCode(error);

      switch (errorCode) {
        case "INVALID_CHARACTERS":
          setFormError(t("settings.invalidCharacters"));
          enqueueSnackbar(t("settings.invalidCharacters"), {
            variant: "error",
          });
          break;
        case "HOSTNAME_ALREADY_EXISTS":
          setFormError(t("settings.hostnameInUse"));
          enqueueSnackbar(t("settings.hostnameInUse"), {
            variant: "error",
          });
          break;
        case "SUBDOMAIN_REQUIRED":
          setFormError(t("settings.subdomainRequired"));
          enqueueSnackbar(t("settings.subdomainRequired"), {
            variant: "error",
          });
          break;
        case "SUBDOMAIN_TOO_LONG":
          setFormError(t("settings.subdomainTooLong"));
          enqueueSnackbar(t("settings.subdomainTooLong"), {
            variant: "error",
          });
          break;
        case "SUBDOMAIN_NOT_ALLOWED":
          setFormError(t("settings.subdomainNotAllowed"));
          enqueueSnackbar(t("settings.subdomainNotAllowed"), {
            variant: "error",
          });
          break;
        default:
          enqueueSnackbar(t("common:errors.fetchError"), {
            variant: "error",
          });
          break;
      }

      setStatus(Status.Enabled);
    }
  }

  if (status === Status.Loading) {
    return (
      <>
        <SectionHeader title={t("settings.title")}>
          <Typography style={{ maxWidth: "75%" }}>
            {t("settings.subTitle")}
          </Typography>
        </SectionHeader>
        <MxheroSharesSkeleton />
      </>
    );
  }

  return (
    <>
      <SectionHeader title={t("settings.title")}>
        <Typography style={{ maxWidth: "75%" }}>
          {t("settings.subTitle")}
        </Typography>
      </SectionHeader>

      <Paper className={classes.container}>
        <Box display="flex" flexDirection="column" gridGap="1rem">
          {status === Status.Disabled && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography variant="subtitle2">
                {t("settings.setup")}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {t("settings.setupSubtitle")}
              </Typography>

              <Button
                style={{ marginTop: 10, marginBottom: 10, alignSelf: "center" }}
                color="primary"
                variant="contained"
                disableElevation
                onClick={() => setStatus(Status.Enabled)}
              >
                {t("settings.setup")}
              </Button>
            </div>
          )}
          {(status === Status.Enabled || status === Status.Saving) && (
            <>
              <Box
                mb="1rem"
                display="flex"
                flexDirection="column"
                gridGap="1.5rem"
              >
                <SubdomainInput
                  isReadOnly={isEdit}
                  error={formError}
                  disabled={status === Status.Saving}
                  value={form.subdomain}
                  onChange={updateForm}
                />
                <Divider />
                <RegionOptions
                  value={form.region}
                  onChange={updateForm}
                  disabled={isEdit || status === Status.Saving}
                />
                <Divider />
                <RetentionOptions
                  disabled={status === Status.Saving}
                  value={form.retention}
                  onChange={updateForm}
                />
                <Divider />
                <AuthenticationOptions
                  options={{
                    externalAuthenticationMethod:
                      form.externalAuthenticationMethod,
                    organizationAuthenticationMethod:
                      form.organizationAuthenticationMethod,
                  }}
                  onChange={updateForm}
                  disabled={status === Status.Saving}
                />
                <Divider />
                <EndUserPermissions
                  permissions={form.ownerPermissions}
                  onChange={updateForm}
                />
              </Box>
              <ButtonWithLoading
                loading={status === Status.Saving}
                variant="contained"
                disableElevation
                onClick={onSubmit}
                disabled={!canSubmit(form)}
              >
                {isEdit
                  ? t("settings.submitBtn.edit")
                  : t("settings.inputAction")}
              </ButtonWithLoading>
            </>
          )}
        </Box>
      </Paper>
    </>
  );
}

function initForm() {
  const { email } = getUser();
  const emailDomain = email.split("@")[1];

  return {
    retention: "ONE_YEAR" as MxheroShareConfig["retention"],
    subdomain: emailDomain.split(".")[0],
    region: "US" as MxheroShareConfig["region"],
    externalAuthenticationMethod: [],
    organizationAuthenticationMethod: [],
    ownerPermissions: [],
  };
}

function canSubmit(form: MxheroShareConfig) {
  const subdomain = form.subdomain;

  if (isBlank(subdomain)) return false;

  const externalAuth = form.externalAuthenticationMethod;

  if (!externalAuth.length) return false;

  const orgAuth = form.organizationAuthenticationMethod;

  if (!orgAuth.length) return false;

  return true;
}
